import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { EyeMark, OrnRule } from "@/components/Ornaments";
import { Seo } from "@/components/Seo";
import { FACEBOOK_POSTS, type FacebookPhoto } from "@/content/facebookPosts";
import type { PhotoCredit } from "@/content/postOverrides";
import type { LanguageBinding } from "@/hooks/useLanguage";
import { cx } from "@/lib/classnames";
import { formatLongDate } from "@/lib/dates";
import { isOverrideExcluded, mergePost } from "@/lib/postOverrides";

/**
 * Small attribution strip rendered beneath a stock / Wikimedia photo.
 * Renders nothing when no credit is supplied (original FB-archive photos).
 * The licence text is a link to the license deed, and the source link
 * points back to the Commons file page so reuse is traceable.
 */
const PhotoAttribution: React.FC<{
  credit: PhotoCredit | undefined;
  isRTL: boolean;
}> = ({ credit, isRTL }) => {
  if (!credit) return null;
  const sourceName = credit.source ?? "Wikimedia Commons";
  const linkStyle: React.CSSProperties = { color: "var(--gold-2)" };
  return (
    <div
      dir="ltr"
      className={cx(
        isRTL ? "ff-ar-sans" : "ff-en-sc tracked-wide",
        "text-[11px] opacity-80 mt-1",
      )}
      style={{ color: "var(--muted-dark)" }}
    >
      © {credit.author} ·{" "}
      {credit.licenseUrl ? (
        <a
          href={credit.licenseUrl}
          target="_blank"
          rel="noopener noreferrer license"
          style={linkStyle}
        >
          {credit.license}
        </a>
      ) : (
        credit.license
      )}
      {credit.sourceUrl && (
        <>
          {" · "}
          <a
            href={credit.sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={linkStyle}
          >
            {sourceName}
          </a>
        </>
      )}
    </div>
  );
};

/**
 * Full-screen, in-site image viewer. Opens when a gallery thumbnail is
 * clicked. Prev/next arrows (+ keyboard arrow keys), close button (+ Esc).
 * Clicking the backdrop closes the modal; clicking the image itself does not.
 */
const Lightbox: React.FC<{
  photos: readonly FacebookPhoto[];
  index: number;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
  isRTL: boolean;
}> = ({ photos, index, onClose, onPrev, onNext, isRTL }) => {
  const base = import.meta.env.BASE_URL;
  const photo = photos[index];
  const hasPrev = index > 0;
  const hasNext = index < photos.length - 1;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") {
        // In RTL the left arrow feels like "next" to many users, but we keep
        // the conventional direction: left = lower index, right = higher.
        if (hasPrev) onPrev();
      } else if (e.key === "ArrowRight") {
        if (hasNext) onNext();
      }
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose, onPrev, onNext, hasPrev, hasNext]);

  const btnStyle: React.CSSProperties = {
    background: "rgba(255,255,255,.08)",
    color: "#fff3df",
    border: "1px solid rgba(255,255,255,.25)",
    backdropFilter: "blur(4px)",
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Photo viewer"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8"
      style={{ background: "rgba(5,4,3,.94)" }}
      onClick={onClose}
    >
      <img
        src={`${base}${photo.full.replace(/^\//, "")}`}
        alt={photo.alt || ""}
        className="max-h-[88vh] max-w-[92vw] object-contain select-none"
        onClick={(e) => e.stopPropagation()}
        draggable={false}
      />

      {/* Close */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="absolute top-4 right-4 h-11 w-11 rounded-full flex items-center justify-center transition-opacity hover:opacity-80"
        style={btnStyle}
        aria-label="Close"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
          <path d="M3 3 L13 13 M13 3 L3 13" />
        </svg>
      </button>

      {/* Counter */}
      {photos.length > 1 && (
        <div
          className="absolute top-4 left-4 px-3 py-1.5 rounded-full text-[12px] ff-en-body tabular-nums"
          style={btnStyle}
        >
          {index + 1} / {photos.length}
        </div>
      )}

      {/* Prev */}
      {photos.length > 1 && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (hasPrev) onPrev();
          }}
          disabled={!hasPrev}
          className={cx(
            "absolute left-4 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full flex items-center justify-center transition-opacity",
            hasPrev ? "hover:opacity-80" : "opacity-30 cursor-not-allowed",
          )}
          style={btnStyle}
          aria-label={isRTL ? "الصورة السابقة" : "Previous photo"}
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M11 3 L5 9 L11 15" />
          </svg>
        </button>
      )}

      {/* Next */}
      {photos.length > 1 && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (hasNext) onNext();
          }}
          disabled={!hasNext}
          className={cx(
            "absolute right-4 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full flex items-center justify-center transition-opacity",
            hasNext ? "hover:opacity-80" : "opacity-30 cursor-not-allowed",
          )}
          style={btnStyle}
          aria-label={isRTL ? "الصورة التالية" : "Next photo"}
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M7 3 L13 9 L7 15" />
          </svg>
        </button>
      )}
    </div>
  );
};

function excerpt(text: string, max: number): string {
  const clean = text.replace(/\s+/g, " ").trim();
  if (clean.length <= max) return clean;
  return clean.slice(0, max).trimEnd() + "…";
}

export const PostDetail: React.FC<{ language: LanguageBinding }> = ({ language }) => {
  const { t, lang, isRTL } = language;
  const { slug } = useParams<{ slug: string }>();
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const openLightbox = useCallback((i: number) => setLightboxIndex(i), []);
  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const prevLightbox = useCallback(
    () => setLightboxIndex((i) => (i === null || i <= 0 ? i : i - 1)),
    [],
  );
  const nextLightbox = useCallback(
    () =>
      setLightboxIndex((i) =>
        i === null ? i : i + 1, // bounds check happens in Lightbox via disabled state
      ),
    [],
  );

  // Filter by current UI language and apply editorial overrides before
  // building prev/next so navigation stays within the visible archive.
  // Unknown slugs — or slugs whose content only exists in the other language
  // — fall through to /posts.
  const sorted = useMemo(() => {
    const arr = FACEBOOK_POSTS.filter((p) => !isOverrideExcluded(p.slug))
      .map(mergePost)
      .filter((p) =>
        lang === "ar" ? p.textAr.length > 0 : p.textEn.length > 0,
      );
    arr.sort((a, b) => {
      const ta = a.dateIso ? Date.parse(a.dateIso) : 0;
      const tb = b.dateIso ? Date.parse(b.dateIso) : 0;
      if (tb !== ta) return tb - ta;
      return b.sourceEntryIndex - a.sourceEntryIndex;
    });
    return arr;
  }, [lang]);

  const idx = sorted.findIndex((p) => p.slug === slug);
  if (idx === -1) return <Navigate to="/posts" replace />;

  const post = sorted[idx];
  const prev = sorted[idx - 1]; // newer
  const next = sorted[idx + 1]; // older
  const base = import.meta.env.BASE_URL;
  const longDate = formatLongDate(post.dateIso, lang);
  const bodyText = lang === "ar" ? post.textAr : post.textEn;
  const titleText = (lang === "ar" ? post.titleAr : post.titleEn) || excerpt(bodyText, 70);
  const seoTitle = titleText.slice(0, 60);
  const seoDescription = excerpt(bodyText, 160);
  const ogImage = post.photos[0]?.full ? `${base}${post.photos[0].full.replace(/^\//, "")}` : undefined;

  return (
    <>
      <Seo
        title={`${seoTitle} · ${t.site.name}`}
        description={seoDescription}
        lang={lang}
        path={`/posts/${post.slug}`}
        ogImage={ogImage}
      />

      <article
        className="relative"
        style={{ background: "var(--ink)", color: "var(--parchment)" }}
      >
        <div className="grain" />
        <div className="max-w-[840px] mx-auto px-6 md:px-10 py-20 md:py-24 relative">
          <Link
            to="/posts"
            className={cx(
              isRTL ? "ff-ar-sans" : "ff-en-sc tracked-wide",
              "text-[11px] inline-block mb-8 opacity-70 hover:opacity-100",
            )}
            style={{ color: "var(--gold-2)" }}
          >
            ← {t.postsPage.backToIndex}
          </Link>

          <div
            className={cx(
              isRTL ? "ff-ar-sans" : "ff-en-sc tracked-widest",
              "text-[11px] mb-4",
            )}
            style={{ color: "var(--vermillion-2)" }}
          >
            {t.postsPage.postKicker}
          </div>
          <h1
            className={cx(
              lang === "ar" ? "ff-ar-serif" : "ff-en-serif",
              "leading-[1.25] mb-6",
            )}
            dir={lang === "ar" ? "rtl" : "ltr"}
            lang={lang}
            style={{
              fontSize: "clamp(1.6rem, 3.2vw, 2.6rem)",
              fontWeight: 600,
            }}
          >
            {titleText}
          </h1>
          <div
            className={cx(
              isRTL ? "ff-ar-sans" : "ff-en-sc tracked-wide",
              "text-[12px]",
            )}
            style={{ color: "var(--muted-dark)" }}
          >
            {longDate}
          </div>

          <div className="my-10 max-w-md" style={{ color: "var(--gold)" }}>
            <OrnRule />
          </div>

          <div
            dir={lang === "ar" ? "rtl" : "ltr"}
            lang={lang}
            className={cx(
              lang === "ar" ? "ff-ar-serif" : "ff-en-body",
              "text-lg md:text-xl leading-[2] space-y-6",
            )}
            style={{ color: "var(--parchment)" }}
          >
            {bodyText.split(/\n{2,}/).map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>

          {post.photos.length > 0 && (
            <div className="mt-14">
              <div
                className={cx(
                  isRTL ? "ff-ar-sans" : "ff-en-sc tracked-widest",
                  "text-[11px] mb-5",
                )}
                style={{ color: "var(--gold-2)" }}
              >
                {t.postsPage.photoCount(post.photos.length)}
              </div>
              {/*
                Gallery policy
                ---------------
                - photoCaptions (from overrides) present: photo-essay layout,
                  one image per row with its AR/EN caption below — the post
                  becomes a narrated series rather than a text + grid.
                - Single-image posts: show the whole image at natural aspect
                  (no crop, no letterbox), capped at 540px tall so it doesn't
                  dominate. Uses the high-res .full source.
                - Multi-image posts: 2-col grid of 4:3 thumbnails. Thumbnails
                  are already smart-cropped to 4:3 by the importer, so fitting
                  them in a 4:3 cell with object-cover produces no letterbox.
                - In all cases, clicking an image opens the in-site lightbox.
              */}
              {post.photoCaptions && post.photoCaptions.length > 0 ? (
                <div className="space-y-10">
                  {post.photos.map((ph, i) => {
                    const cap = post.photoCaptions?.[i];
                    const credit = post.photoCredits?.[i];
                    return (
                      <figure key={ph.full} className="space-y-3">
                        <button
                          type="button"
                          onClick={() => openLightbox(i)}
                          className="block w-full overflow-hidden cursor-zoom-in"
                          style={{ border: "1px solid var(--hairline-dark)" }}
                        >
                          <img
                            src={`${base}${ph.full.replace(/^\//, "")}`}
                            alt={
                              ph.alt ||
                              (lang === "ar" ? cap?.ar : cap?.en) ||
                              t.postsPage.photoAlt(
                                i + 1,
                                post.photos.length,
                                longDate,
                              )
                            }
                            loading="lazy"
                            decoding="async"
                            className="max-h-[520px] w-auto max-w-full mx-auto transition-opacity duration-300 hover:opacity-95"
                          />
                        </button>
                        {(cap?.ar || cap?.en) && (
                          <figcaption
                            className="space-y-1"
                            style={{ color: "var(--muted-dark)" }}
                          >
                            {cap?.ar && (
                              <div
                                dir="rtl"
                                lang="ar"
                                className="ff-ar-serif text-base md:text-lg leading-relaxed"
                              >
                                {cap.ar}
                              </div>
                            )}
                            {cap?.en && (
                              <div
                                dir="ltr"
                                lang="en"
                                className="ff-en-body italic text-sm md:text-base leading-relaxed"
                              >
                                {cap.en}
                              </div>
                            )}
                          </figcaption>
                        )}
                        <PhotoAttribution credit={credit} isRTL={isRTL} />
                      </figure>
                    );
                  })}
                </div>
              ) : post.photos.length === 1 ? (
                <figure>
                  <button
                    type="button"
                    onClick={() => openLightbox(0)}
                    className="block w-full overflow-hidden cursor-zoom-in"
                    style={{ border: "1px solid var(--hairline-dark)" }}
                  >
                    <img
                      src={`${base}${post.photos[0].full.replace(/^\//, "")}`}
                      alt={
                        post.photos[0].alt ||
                        t.postsPage.photoAlt(1, 1, longDate)
                      }
                      loading="lazy"
                      decoding="async"
                      className="max-h-[540px] w-auto max-w-full mx-auto transition-opacity duration-300 hover:opacity-95"
                    />
                  </button>
                  <PhotoAttribution
                    credit={post.photoCredits?.[0]}
                    isRTL={isRTL}
                  />
                </figure>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {post.photos.map((ph, i) => (
                    <button
                      key={ph.full}
                      type="button"
                      onClick={() => openLightbox(i)}
                      className="block aspect-[4/3] overflow-hidden cursor-zoom-in"
                      style={{ border: "1px solid var(--hairline-dark)" }}
                    >
                      <img
                        src={`${base}${ph.thumb.replace(/^\//, "")}`}
                        alt={
                          ph.alt ||
                          t.postsPage.photoAlt(i + 1, post.photos.length, longDate)
                        }
                        loading="lazy"
                        decoding="async"
                        className="h-full w-full object-cover transition-transform duration-500 hover:scale-[1.03]"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {lightboxIndex !== null && post.photos.length > 0 && (
            <Lightbox
              photos={post.photos}
              index={Math.min(lightboxIndex, post.photos.length - 1)}
              onClose={closeLightbox}
              onPrev={prevLightbox}
              onNext={nextLightbox}
              isRTL={isRTL}
            />
          )}

          {post.sourceUrl && (
            <div className="mt-12 text-center">
              <a
                href={post.sourceUrl}
                target="_blank"
                rel="noreferrer"
                className={cx(
                  isRTL ? "ff-ar-sans" : "ff-en-sc tracked-wide",
                  "text-[12px]",
                )}
                style={{ color: "var(--gold-2)" }}
              >
                {t.postsPage.viewOriginal} ↗
              </a>
            </div>
          )}

          <nav
            className="mt-16 grid grid-cols-2 gap-6 pt-8"
            style={{ borderTop: "1px solid var(--hairline-dark)" }}
            aria-label="post navigation"
          >
            <div>
              {prev ? (
                <Link to={`/posts/${prev.slug}`} className="group block">
                  <div
                    className={cx(
                      isRTL ? "ff-ar-sans" : "ff-en-sc tracked-wide",
                      "text-[10px] mb-1",
                    )}
                    style={{ color: "var(--vermillion-2)" }}
                  >
                    ← {t.postsPage.prev}
                  </div>
                  <div
                    dir={lang === "ar" ? "rtl" : "ltr"}
                    lang={lang}
                    className={cx(
                      lang === "ar" ? "ff-ar-serif" : "ff-en-serif",
                      "text-sm leading-snug line-clamp-2",
                    )}
                    style={{ color: "var(--parchment)" }}
                  >
                    {(lang === "ar" ? prev.titleAr : prev.titleEn) ||
                      excerpt(lang === "ar" ? prev.textAr : prev.textEn, 80)}
                  </div>
                </Link>
              ) : (
                <span />
              )}
            </div>
            <div className="text-end">
              {next ? (
                <Link to={`/posts/${next.slug}`} className="group block">
                  <div
                    className={cx(
                      isRTL ? "ff-ar-sans" : "ff-en-sc tracked-wide",
                      "text-[10px] mb-1",
                    )}
                    style={{ color: "var(--vermillion-2)" }}
                  >
                    {t.postsPage.next} →
                  </div>
                  <div
                    dir={lang === "ar" ? "rtl" : "ltr"}
                    lang={lang}
                    className={cx(
                      lang === "ar" ? "ff-ar-serif" : "ff-en-serif",
                      "text-sm leading-snug line-clamp-2",
                    )}
                    style={{ color: "var(--parchment)" }}
                  >
                    {(lang === "ar" ? next.titleAr : next.titleEn) ||
                      excerpt(lang === "ar" ? next.textAr : next.textEn, 80)}
                  </div>
                </Link>
              ) : (
                <span />
              )}
            </div>
          </nav>

          <div className="mt-14 text-center" style={{ color: "var(--gold)" }}>
            <EyeMark size={28} />
          </div>
        </div>
      </article>
    </>
  );
};
